// src/cxFeedback/index.js  →  dist/cxFeedback.js  (global: CxFeedback)
//
// Customer feedback scorecard (#7 slice 2) for the managers app. Reads
// /api/admin/customer-feedback (HO sees all; an area manager sees only their
// area) and shows the overall score + per-store breakdown (worst first) + recent
// comments. Read-only. Auth = the admin Bearer via DB.getToken/DB.workerUrl.

function esc(s) {
  return (typeof window !== 'undefined' && window.escapeHtml)
    ? window.escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Pure helpers (exported for tests).
export function ratingColor(avg) { return avg == null ? '#6b7280' : avg >= 8 ? '#059669' : avg >= 6 ? '#D97706' : '#DC2626'; }
export function npsColor(nps) { return nps == null ? '#6b7280' : nps >= 50 ? '#059669' : nps >= 0 ? '#D97706' : '#DC2626'; }
export function fmtWhen(iso) { const s = String(iso || ''); return s.slice(0, 10); }

// ── B2: week / month / quarter grid — pure helpers (exported for tests) ──────
// The period toggle values. Default is month.
export const PERIODS = ['week', 'month', 'quarter'];

// How many period columns the grid shows, by period (readability cap):
// last 8 weeks / 6 months / 4 quarters.
export function colCap(period) {
  return period === 'week' ? 8 : period === 'quarter' ? 4 : 6;
}

// Score → colour band. Thresholds are inclusive at the top edge:
//   >= 8.5 green, 7.0–8.4 amber, < 7.0 red. A null/undefined avg → null (no colour).
export function scoreBand(avg) {
  if (avg == null || !isFinite(Number(avg))) return null;
  const n = Number(avg);
  if (n >= 8.5) return 'green';
  if (n >= 7.0) return 'amber';
  return 'red';
}
// Soft background + dark text per band (accessible: dark text on a pale fill).
export function bandStyle(band) {
  if (band === 'green') return { bg: 'var(--ok-bg)', fg: 'var(--ok-deep)' };
  if (band === 'amber') return { bg: 'var(--warn-bg)', fg: 'var(--warn-deep)' };
  if (band === 'red')   return { bg: 'var(--danger-bg)', fg: 'var(--danger-deep)' };
  return { bg: 'transparent', fg: 'var(--tiny)' };   // no reviews that period
}

// Column label from a bucket label: '2026-W27' → 'W27', '2026-07' → 'Jul', '2026-Q3' → 'Q3'.
export function colLabel(bucketLabel) {
  const s = String(bucketLabel || '');
  const wk = s.match(/^\d{4}-(W\d{1,2})$/);
  if (wk) return wk[1];
  const qt = s.match(/^\d{4}-(Q\d)$/);
  if (qt) return qt[1];
  const mo = s.match(/^(\d{4})-(\d{2})$/);
  if (mo) {
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const idx = Number(mo[2]) - 1;
    return names[idx] || s;
  }
  return s;
}

// The headline delta: compare the two NEWEST buckets (buckets arrive newest-first).
// Returns null if fewer than two buckets. `avg` deltas are rounded to 1 dp.
export function periodDelta(buckets) {
  const b = Array.isArray(buckets) ? buckets : [];
  if (b.length < 2) return null;
  const cur = b[0], prev = b[1];
  const d = (a, x) => (a == null || x == null) ? null : Math.round((Number(a) - Number(x)) * 10) / 10;
  return {
    avg: d(cur.avg, prev.avg),
    nps: (cur.nps == null || prev.nps == null) ? null : Math.round(Number(cur.nps) - Number(prev.nps)),
    count: (cur.count || 0) - (prev.count || 0),
  };
}

// Pivot the timeline buckets into a rows × columns matrix for the grid.
//   buckets: newest-first (as the endpoint returns them).
//   maxCols: cap the number of period columns kept (see colCap).
// Returns:
//   { cols: [{ label, colLabel, periodStart }],   // oldest → newest, left → right
//     allRow: { store:'All stores', all:true, cells:[{avg,count}|null], recentAvg },
//     rows:   [{ store, cells:[{avg,count}|null per col], recentAvg }] }  // worst recent-avg first
// A store with no reviews in a period → a null cell. `recentAvg` uses the store's
// avg in the NEWEST kept column (falling back to the most recent column it has data
// in) so the worst-recent store sorts to the top.
export function pivotTimeline(buckets, maxCols) {
  const all = Array.isArray(buckets) ? buckets.slice() : [];
  // Keep only the most recent `maxCols`, then order oldest→newest for display.
  const kept = all.slice(0, Math.max(0, maxCols || all.length));
  const cols = kept.slice().reverse().map((b) => ({
    label: b.label, colLabel: colLabel(b.label), periodStart: b.periodStart,
  }));
  const newestIdx = cols.length - 1;   // rightmost column = newest kept period

  // Union of stores across the kept buckets.
  const storeSet = new Set();
  kept.forEach((b) => (b.byStore || []).forEach((s) => storeSet.add(s.store)));

  // Per bucket, index byStore by store for O(1) cell lookup.
  const byLabelStore = {};
  kept.forEach((b) => {
    const m = {};
    (b.byStore || []).forEach((s) => { m[s.store] = s; });
    byLabelStore[b.label] = m;
  });
  const topLevelByLabel = {};
  kept.forEach((b) => { topLevelByLabel[b.label] = b; });

  const cellFor = (label, store) => {
    const s = byLabelStore[label] && byLabelStore[label][store];
    return (s && s.avg != null) ? { avg: s.avg, count: s.count || 0 } : null;
  };
  // recentAvg = the store's avg in the newest column that HAS data (rightmost first).
  const recentAvgOf = (cells) => {
    for (let i = cells.length - 1; i >= 0; i--) { if (cells[i] && cells[i].avg != null) return Number(cells[i].avg); }
    return null;
  };

  const rows = [...storeSet].map((store) => {
    const cells = cols.map((c) => cellFor(c.label, store));
    return { store, cells, recentAvg: recentAvgOf(cells) };
  });
  // Worst recent-avg first; stores with no recent data sink to the bottom.
  rows.sort((a, b) => {
    const av = a.recentAvg, bv = b.recentAvg;
    if (av == null && bv == null) return String(a.store).localeCompare(String(b.store));
    if (av == null) return 1;
    if (bv == null) return -1;
    return av - bv;
  });

  // All-stores summary row uses each bucket's top-level avg/count.
  const allCells = cols.map((c) => {
    const b = topLevelByLabel[c.label];
    return (b && b.avg != null) ? { avg: b.avg, count: b.count || 0 } : null;
  });
  const allRow = { store: 'All stores', all: true, cells: allCells, recentAvg: recentAvgOf(allCells) };

  return { cols, allRow, rows, newestIdx };
}

function api(path, opts) {
  opts = opts || {};
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (opts.body) headers['Content-Type'] = 'application/json';
  return fetch(base + path, { cache: 'no-store', method: opts.method || 'GET', headers: headers, body: opts.body });
}
function root() { return document.getElementById('cxFeedbackRoot'); }

// ── Filters (v123.27) — Country / Area manager / Month / Rating ──────────────
// Applied SERVER-SIDE (the scores respond too, not just the comment list), so a
// change just re-fetches with query params. Mirrors the Store Visits tab.
let _filters = { country: '', area: '', month: '', rating: '', store: '' };
export function setFilter(key, val) {
  if (!Object.prototype.hasOwnProperty.call(_filters, key)) return;
  _filters[key] = val || '';
  render();
}
export function clearFilters() { _filters = { country: '', area: '', month: '', rating: '', store: '' }; render(); }
function filterQS() {
  const parts = [];
  Object.keys(_filters).forEach((k) => { if (_filters[k]) parts.push(k + '=' + encodeURIComponent(_filters[k])); });
  return parts.length ? ('?' + parts.join('&')) : '';
}
function anyFilterActive() { return !!(_filters.country || _filters.area || _filters.month || _filters.rating || _filters.store); }

// ── v130.8 — sub-tabs (spec #1214: "each bit should have its own sub tab") ───
// One long page became five pills. State is module-local; switching re-renders
// from the already-fetched data where possible (the fetch is per-render anyway).
let _tab = 'scorecard';   // scorecard | reviews | action | insights | links
export function setTab(t) {
  if (['scorecard', 'reviews', 'action', 'insights', 'links'].indexOf(t) === -1) return;
  _tab = t;
  render();
}
function navHtml(actionCount, isHO) {
  const pill = (t, label, badge) => {
    const on = t === _tab;
    return '<button onclick="CxFeedback.setTab(\'' + t + '\')" style="border:1px solid ' + (on ? 'var(--text)' : 'var(--border-strong)') +
      ';background:' + (on ? 'var(--text)' : 'var(--card)') + ';color:' + (on ? 'var(--card)' : 'var(--text)') +
      ';padding:7px 14px;border-radius:999px;font-size:13px;font-weight:700;cursor:pointer">' + label +
      (badge ? ' <span style="background:#DC2626;color:#fff;border-radius:999px;padding:1px 7px;font-size:11px;font-weight:800">' + badge + '</span>' : '') +
      '</button>';
  };
  return '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px">' +
    pill('scorecard', '📊 Scorecard') +
    pill('reviews', '💬 Reviews') +
    pill('action', '⚠️ Needs action', actionCount || 0) +
    pill('insights', '🤖 Insights') +
    pill('links', '🔗 Store QR links') +
  '</div>';
}

// ── v131.68 — the per-store survey link ──────────────────────────────────────
// Matt, 15 Sep: "the qr code doesnt have the store in the link? you have you
// choose it from a drop down?" — correct, and nothing in the app ever built
// those links, so whoever produced the QR codes used the bare domain. Every
// customer therefore answers three dropdowns (country, city, store) before
// they can rate anything, on a survey where each extra step costs completions.
//
// A link that names the store skips all three: the survey opens straight on
// the rating, and the store is recorded exactly rather than self-reported.
export const SURVEY_BASE = 'https://elctoysfeedback.com/';

export function surveyLinkFor(store) {
  return SURVEY_BASE + '?store=' + encodeURIComponent(String(store || ''));
}

export function copySurveyLink(store) {
  copyText(surveyLinkFor(store), 'Link copied — ' + store);
}

// v132.66: and the store's own Google review link beside it, for the same
// per-store print material (a QR on a card at the till, a window sticker; a
// per-store QR on the receipt is a till change, so a recommendation). It is
// the link set in Admin -> Stores, the one the survey's thank-you screen
// offers, read from the map the admin boot fills (src/uploads/stores.js).
// A store without one says so and points at where to set it; it never guesses.
// Dubai Mall and Dubai Mall 2 share one Google listing, so they show one link.
export function googleLinkFor(store) {
  const map = (typeof window !== 'undefined' && window._storeGoogleMap) || {};
  return map[store] || '';
}

export function copyGoogleLink(store) {
  const url = googleLinkFor(store);
  if (!url) {
    if (typeof showToast === 'function') showToast('No Google review link set for ' + store + ' (Admin, Stores)', 'error');
    return;
  }
  copyText(url, 'Google link copied: ' + store);
}

function copyText(url, toast) {
  const done = () => { if (typeof showToast === 'function') showToast(toast, 'success'); };
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(url).then(done, () => window.prompt('Copy this link:', url)); return; }
  } catch (e) { /* fall through */ }
  window.prompt('Copy this link:', url);
}

function linksHtml(stores) {
  const cell = 'padding:8px 10px;border-bottom:1px solid var(--border);';
  const mono = cell + 'font-family:ui-monospace,Menlo,Consolas,monospace;font-size:12px;color:var(--text);word-break:break-all';
  const btn = 'border:1px solid var(--border-strong);background:var(--card);border-radius:8px;padding:5px 12px;font-size:12px;font-weight:700;cursor:pointer;margin-top:4px';
  const arg = (s) => '\'' + esc(String(s).replace(/'/g, "\'")) + '\'';
  const rows = (stores || []).map((s) => {
    const g = googleLinkFor(s);
    return '<tr>' +
      '<td translate="no" style="' + cell + 'font-weight:600">' + esc(s) + '</td>' +
      '<td style="' + mono + '">' + esc(surveyLinkFor(s)) +
        '<br><button onclick="CxFeedback.copySurveyLink(' + arg(s) + ')" style="' + btn + '">Copy survey link</button></td>' +
      '<td style="' + mono + '">' + (g
        ? esc(g) + '<br><button onclick="CxFeedback.copyGoogleLink(' + arg(s) + ')" style="' + btn + '">Copy Google link</button>'
        : '<span style="color:var(--tiny)">Not set. Add it in Admin, Stores.</span>') + '</td>' +
    '</tr>';
  }).join('');
  return '<div style="font-size:13px;color:var(--text);line-height:1.6;margin-bottom:14px">' +
      'A link that names the store opens the survey <b>straight on the rating</b> — no country, city or store ' +
      'dropdowns — and records the store exactly instead of asking the customer to pick it.' +
      '<br><br><b>Not for the receipt.</b> The till prints one link for every store, so receipt customers ' +
      'will always use the dropdowns. These are for anything you control per store: a card at the till, ' +
      'a window sticker, a shelf talker, a link in a store\'s own email.' +
      '<br><b>The Google review link</b> is the store\'s own listing, the same link the survey offers after a ' +
      'customer submits. It goes wherever you want a customer to review the store directly on Google. ' +
      'Dubai Mall and Dubai Mall 2 share one listing, so they show the same link.' +
      '<br><b>To use one:</b> copy it and make a QR code with whatever tool marketing uses for print.' +
    '</div>' +
    '<table style="border-collapse:collapse;width:100%;font-size:13px">' +
      '<tr><th align="left" style="padding:8px 10px;border-bottom:2px solid var(--border)">Store</th>' +
      '<th align="left" style="padding:8px 10px;border-bottom:2px solid var(--border)">Survey link</th>' +
      '<th align="left" style="padding:8px 10px;border-bottom:2px solid var(--border)">Google review link</th></tr>' + rows + '</table>';
}

// ── B2: the week/month/quarter period view ───────────────────────────────────
// The chosen period survives a tab re-render (module var) AND a full reload
// (localStorage). Default is month. Switching re-fetches the timeline only —
// the scorecard below is untouched.
const PERIOD_LS_KEY = 'cxPeriod';
function initialPeriod() {
  try {
    const v = (typeof localStorage !== 'undefined') ? localStorage.getItem(PERIOD_LS_KEY) : null;
    if (v && PERIODS.indexOf(v) !== -1) return v;
  } catch (e) { /* private mode / no storage */ }
  return 'month';
}
let _period = initialPeriod();
export function setPeriod(p) {
  if (PERIODS.indexOf(p) === -1 || p === _period) return;
  _period = p;
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(PERIOD_LS_KEY, p); } catch (e) { /* ignore */ }
  renderTimeline();
}
function timelineRoot() { return document.getElementById('cxTimelineRoot'); }
// Last 12 months as [{ v:'YYYY-MM', label:'Jun 2026' }].
function recentMonths() {
  const out = []; const now = new Date();
  for (let i = 0; i < 12; i++) {
    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const v = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0');
    out.push({ v: v, label: dt.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) });
  }
  return out;
}
function selOpt(val, label, cur) {
  return '<option value="' + esc(val) + '"' + (String(cur) === String(val) ? ' selected' : '') + '>' + esc(label) + '</option>';
}
function filterBar(data) {
  const fo = (data && data.filterOptions) || { countries: [], areas: [] };
  const one = (id, label, opts) =>
    '<div style="display:flex;flex-direction:column;gap:2px">' +
      '<span style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:var(--sub)">' + esc(label) + '</span>' +
      '<select onchange="CxFeedback.setFilter(\'' + id + '\', this.value)" style="padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px;min-width:130px;background:var(--card)">' + opts + '</select>' +
    '</div>';
  const countryOpts = selOpt('', 'All countries', _filters.country) + (fo.countries || []).map((c) => selOpt(c, c, _filters.country)).join('');
  const areaOpts = selOpt('', 'All area managers', _filters.area) + (fo.areas || []).map((a) => selOpt(a, a, _filters.area)).join('');
  // v130.8 — the Store filter (spec #1214)
  const storeOpts = selOpt('', 'All stores', _filters.store) + (fo.stores || []).map((s) => selOpt(s, s, _filters.store)).join('');
  const monthOpts = selOpt('', 'All months', _filters.month) + recentMonths().map((m) => selOpt(m.v, m.label, _filters.month)).join('');
  let ratingOpts = selOpt('', 'All ratings', _filters.rating);
  for (let n = 10; n >= 1; n--) ratingOpts += selOpt(String(n), n + ' / 10', _filters.rating);
  const clear = anyFilterActive()
    ? '<button onclick="CxFeedback.clearFilters()" style="align-self:flex-end;padding:6px 12px;border:1px solid var(--border-strong);background:var(--card);border-radius:8px;font-size:13px;cursor:pointer">Clear</button>' : '';
  return '<div style="display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end;margin-bottom:16px">' +
    one('store', 'Store', storeOpts) +
    one('country', 'Country', countryOpts) + one('area', 'Area manager', areaOpts) +
    one('month', 'Month', monthOpts) + one('rating', 'Rating', ratingOpts) + clear + '</div>';
}

// Head Office only: soft-delete a review (e.g. a test or spam one). It vanishes from
// the scorecard + comments but the row is retained (recoverable). The Delete button is
// only rendered for HO (data.scoped === false); the Worker also enforces HO-only.
export async function del(id) {
  if (!id) return;
  if (typeof confirm === 'function' && !confirm('Delete this review? It will be removed from the scorecard (recoverable by Head Office).')) return;
  try {
    const res = await api('/api/admin/customer-feedback/delete', { method: 'POST', body: JSON.stringify({ id: id }) });
    if (!res.ok) throw new Error('status ' + res.status);
    render();   // refresh the scorecard
  } catch (e) {
    if (typeof alert === 'function') alert('Could not delete the review (' + ((e && e.message) || e) + ').');
  }
}

// ── Download comments (CSV) — role-aware, server-gated ───────────────────────
// Fetches /api/admin/customer-feedback/export via api() (ABSOLUTE Worker URL +
// Bearer token — a relative path 404s on Netlify), passing the tab's CURRENT
// filters so the download matches what's on screen. The Worker decides the
// columns: Head Office gets the contact details, an area manager gets
// only Date/Store/Rating/Comment/Country (the PII gate is server-side). We just
// save whatever CSV text comes back as a file.
export async function downloadComments() {
  try {
    const res = await api('/api/admin/customer-feedback/export' + filterQS());
    if (!res.ok) throw new Error('status ' + res.status);
    const csv = await res.text();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const day = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'customer-comments-' + day + '.csv';
    a.click();
    if (typeof URL.revokeObjectURL === 'function') setTimeout(() => URL.revokeObjectURL(a.href), 0);
    if (typeof window !== 'undefined' && window.showToast) window.showToast('Comments downloaded', 'success');
  } catch (e) {
    if (typeof window !== 'undefined' && window.showToast) window.showToast('Could not download the comments (' + ((e && e.message) || e) + ').', 'error');
  }
}

// ── B4: on-demand AI analysis of the current period ──────────────────────────
// "✨ Analyse this period" asks the Worker to read the period's comments and return
// actionable THEMES + individual CALL-OUTS. POST via api() (ABSOLUTE Worker URL +
// Bearer token) with the current period + filters. Renders into its own pane
// (#cxAiRoot) above the scorecard; additive — nothing else on the tab changes.
// A cached result comes back free (shows a "cached" note + a Refresh link); when the
// feature is off / errors / rate-limited the Worker returns { ok:false, reason } and
// we show the reason plainly.
let _aiBusy = false;
function aiRoot() { return document.getElementById('cxAiRoot'); }
function priorityChip(p) {
  const map = { high: { bg: 'var(--danger-bg)', fg: 'var(--danger-deep)' }, medium: { bg: 'var(--warn-bg)', fg: 'var(--warn-deep)' }, low: { bg: 'var(--border)', fg: 'var(--text)' } };
  const s = map[p] || map.low;
  return '<span style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;padding:2px 7px;border-radius:6px;background:' + s.bg + ';color:' + s.fg + '">' + esc(p || 'low') + '</span>';
}
function themeCard(t) {
  const dot = t.sentiment === 'negative' ? '#DC2626' : t.sentiment === 'positive' ? '#059669' : '#D97706';
  const quotes = (t.examples || []).map((q) =>
    '<div translate="no" style="font-size:12px;color:var(--sub);border-inline-start:2px solid var(--border);padding-inline-start:8px;margin-top:4px;font-style:italic">' + esc(q) + '</div>').join('');
  return '<div style="border:1px solid var(--border);border-radius:10px;padding:12px;margin-bottom:8px;background:var(--card)">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;gap:8px">' +
      '<div style="font-weight:800;font-size:14px"><span style="color:' + dot + '">&#9679;</span> <span translate="no">' + esc(t.theme) + '</span></div>' +
      priorityChip(t.priority) +
    '</div>' +
    (t.action ? '<div style="font-size:13px;color:var(--text);margin-top:6px"><b>Action:</b> <span translate="no">' + esc(t.action) + '</span></div>' : '') +
    quotes +
  '</div>';
}
function calloutCard(c) {
  const praise = c.kind === 'praise';
  const bg = praise ? 'var(--adm-ok-pale)' : 'var(--adm-warn-pale)';
  const bar = praise ? '#059669' : '#D97706';
  const badge = praise
    ? '<span style="font-size:10px;font-weight:800;text-transform:uppercase;padding:2px 7px;border-radius:6px;background:var(--ok-bg);color:var(--ok-deep)">Praise</span>'
    : '<span style="font-size:10px;font-weight:800;text-transform:uppercase;padding:2px 7px;border-radius:6px;background:var(--warn-bg);color:var(--warn-deep)">Worth a look</span>';
  const who = esc(c.store || 'Store') + (c.staff ? ' &middot; ' + esc(c.staff) : '');
  return '<div style="border-left:3px solid ' + bar + ';background:' + bg + ';border-radius:0 8px 8px 0;padding:10px 12px;margin-bottom:8px">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;gap:8px">' +
      '<div translate="no" style="font-weight:700;font-size:13px">' + who + '</div>' + badge +
    '</div>' +
    (c.quote ? '<div translate="no" style="font-size:13px;color:var(--text);margin-top:5px;font-style:italic">&ldquo;' + esc(c.quote) + '&rdquo;</div>' : '') +
    (c.why ? '<div translate="no" style="font-size:12px;color:var(--sub);margin-top:5px">' + esc(c.why) + '</div>' : '') +
  '</div>';
}
function renderAiResult(data) {
  const pane = aiRoot();
  if (!pane) return;
  if (!data || data.ok === false) {
    pane.innerHTML = '<div style="border:1px solid var(--adm-warn-line);background:var(--adm-warn-pale);border-radius:10px;padding:12px;color:var(--warn-deep);font-size:13px">' +
      esc((data && data.reason) || 'AI analysis is unavailable right now.') + '</div>';
    return;
  }
  const themes = data.themes || [];
  const callouts = data.callouts || [];
  const when = esc(fmtWhen(data.generated_at));
  const cachedNote = data.cached
    ? ' &middot; <span style="color:var(--sub)">cached</span> &middot; <a href="#" onclick="CxFeedback.analyse(true);return false" style="color:var(--adm-link);text-decoration:none">Refresh</a>'
    : ' &middot; <a href="#" onclick="CxFeedback.analyse(true);return false" style="color:var(--adm-link);text-decoration:none">Refresh</a>';
  const truncNote = data.truncated
    ? '<div style="font-size:11px;color:var(--sub);margin-top:2px">A representative sample of comments was used (all critical ones + a spread of positives).</div>' : '';
  const meta = '<div style="font-size:12px;color:var(--sub);margin-bottom:10px">Generated ' + when +
    ' &middot; ' + (data.comment_count || 0) + ' comments' + cachedNote + '</div>' + truncNote;
  const disclaimer = '<div style="font-size:11px;color:var(--sub);margin:10px 0 12px;line-height:1.5">' +
    'These are conversation starters drawn from customers&rsquo; own words &mdash; not judgements or proof. ' +
    'Use them to recognise good work or gently look into something.</div>';
  const themesBlock = themes.length
    ? '<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--tiny);margin:6px 0 8px">Themes</div>' + themes.map(themeCard).join('')
    : '<div style="font-size:13px;color:var(--sub);margin-bottom:8px">No clear cross-store themes this period.</div>';
  const calloutsBlock = callouts.length
    ? '<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--tiny);margin:16px 0 8px">Call-outs</div>' + callouts.map(calloutCard).join('')
    : '';
  pane.innerHTML = '<div style="border:1px solid var(--border);border-radius:12px;padding:14px;background:var(--surface-2)">' +
    meta + themesBlock + calloutsBlock + disclaimer + '</div>';
}
export async function analyse(refresh) {
  const pane = aiRoot();
  if (!pane || _aiBusy) return;
  _aiBusy = true;
  pane.innerHTML = '<div style="padding:14px;color:var(--tiny);font-size:13px">&#10024; Analysing this period&hellip;</div>';
  try {
    const body = JSON.stringify({ period: _period, refresh: !!refresh });
    const res = await api('/api/admin/customer-feedback/ai-analyse' + filterQS(), { method: 'POST', body: body });
    if (res.status === 401 || res.status === 403) {
      pane.innerHTML = '<div style="padding:12px;color:var(--adm-red-text);font-size:13px">Your session looks expired — sign out and back in.</div>';
      return;
    }
    if (!res.ok) throw new Error('status ' + res.status);
    renderAiResult(await res.json());
  } catch (e) {
    pane.innerHTML = '<div style="padding:12px;color:var(--adm-red-text);font-size:13px">Could not run the analysis (' + esc((e && e.message) || e) + ').</div>';
  } finally {
    _aiBusy = false;
  }
}


// The tab renders in two panes inside #cxFeedbackRoot: the B2 period view
// (grid + summary strip, its own #cxTimelineRoot) ABOVE the existing scorecard
// (#cxScorecardRoot). Each pane fetches + re-renders independently so a period
// switch never disturbs the scorecard and a filter change re-fetches both.
function ensurePanes() {
  const el = root();
  if (!el) return null;
  if (!document.getElementById('cxScorecardRoot')) {
    el.innerHTML = '<div id="cxNavRoot"></div><div id="cxTimelineRoot"></div><div id="cxScorecardRoot"></div>';
  }
  return el;
}

export async function render() {
  if (!ensurePanes()) return;
  // v130.8 — the period timeline is a Scorecard-tab thing; other tabs hide it.
  const tl = timelineRoot();
  if (tl) tl.style.display = (_tab === 'scorecard') ? '' : 'none';
  if (_tab === 'scorecard') renderTimeline();
  const pane = document.getElementById('cxScorecardRoot');
  if (!pane) return;
  pane.innerHTML = '<div style="padding:20px;color:var(--tiny)">Loading customer feedback…</div>';
  let data;
  try {
    const res = await api('/api/admin/customer-feedback' + filterQS());
    if (res.status === 401 || res.status === 403) { pane.innerHTML = '<div style="padding:20px;color:var(--adm-red-text)">Your session looks expired — sign out and back in.</div>'; return; }
    if (!res.ok) throw new Error('status ' + res.status);
    data = await res.json();
  } catch (e) {
    pane.innerHTML = '<div style="padding:20px;color:var(--adm-red-text)">Could not load customer feedback (' + esc((e && e.message) || e) + ').</div>';
    return;
  }
  data = data || {};
  const nav = document.getElementById('cxNavRoot');
  if (nav) nav.innerHTML = navHtml(data.actionCount, data.scoped === false);
  draw(pane, data);
}

// ── v130.9 (CX PR 2) — reply to the customer ─────────────────────────────────
// HO only; only reviews whose customer opted in to a reply carry an
// email. The modal fetches the contact on open (the ONLY payload an address
// rides), prefills a courteous service-recovery draft, and the Worker sends
// from the reviewed store's own address. One reply per review.
export async function openReply(id) {
  let c;
  try {
    const res = await api('/api/admin/customer-feedback/contact?id=' + id);
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || !data.contact) {
      if (typeof window !== 'undefined' && window.showToast) window.showToast((data && data.error) || 'Could not load the contact');
      return;
    }
    c = data.contact;
  } catch (e) { return; }
  const host = document.getElementById('cxReplyModal') || (() => {
    const d = document.createElement('div');
    d.id = 'cxReplyModal';
    document.body.appendChild(d);
    return d;
  })();
  const draft = 'Dear ' + (c.name || 'customer') + ',\n\n' +
    'Thank you for taking the time to leave feedback about your visit to ' + (c.store || 'our store') + '. ' +
    'We are sorry your experience fell short of what you should expect from us.\n\n' +
    'Please feel free to come back to the store with your purchase and receipt and we will be happy to arrange an exchange or refund.\n\n' +
    'Kind regards,\n' + (c.store || 'ELC') + ' team';
  const purposeNote = c.needs_purpose_note
    ? '<div style="background:var(--adm-warn-pale);border:1px solid var(--adm-warn-line);border-radius:8px;padding:8px 12px;font-size:12px;color:var(--warn-deep);margin-bottom:10px">' +
      'This customer left their details (before the survey mentioned feedback replies). Keep the reply strictly about their feedback — no marketing.</div>'
    : '';
  host.innerHTML =
    '<div style="position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:9998" onclick="CxFeedback.closeReply()"></div>' +
    '<div style="position:fixed;top:8%;left:50%;transform:translateX(-50%);width:min(560px,94vw);background:var(--card);border-radius:14px;padding:18px;z-index:9999;box-shadow:0 18px 50px rgba(0,0,0,.25)">' +
      '<div style="font-size:15px;font-weight:800;margin-bottom:2px">↩ Reply to the customer</div>' +
      '<div style="font-size:12px;color:var(--sub);margin-bottom:10px">To <b>' + esc(c.name || '') + '</b> &lt;' + esc(c.email) + '&gt; · about their ' + c.rating + '/10 at ' + esc(c.store || '') + ' · sends from the store\'s own email address</div>' +
      purposeNote +
      (c.comment ? '<div translate="no" style="background:var(--surface-2);border-inline-start:3px solid ' + ratingColor(c.rating) + ';border-radius:6px;padding:8px 12px;font-size:12.5px;color:var(--text);margin-bottom:10px;white-space:pre-wrap">' + esc(c.comment) + '</div>' : '') +
      '<input id="cxReplySubject" type="text" maxlength="200" value="' + esc('About your feedback — ' + (c.store || 'ELC')) + '" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px;margin-bottom:8px">' +
      '<textarea id="cxReplyBody" rows="9" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px;font-family:inherit">' + esc(draft) + '</textarea>' +
      '<div style="display:flex;gap:8px;margin-top:10px;align-items:center">' +
        '<button onclick="CxFeedback.sendReply(' + id + ')" style="background:#059669;color:#fff;border:none;border-radius:9px;padding:9px 16px;font-size:13px;font-weight:700;cursor:pointer">Send reply</button>' +
        '<button onclick="CxFeedback.closeReply()" style="background:var(--card);border:1px solid var(--border-strong);border-radius:9px;padding:9px 16px;font-size:13px;cursor:pointer">Cancel</button>' +
        '<span style="font-size:11px;color:var(--tiny)">One reply per review · recorded on the review and in the store\'s Sent folder</span>' +
      '</div>' +
    '</div>';
}

export function closeReply() {
  const host = document.getElementById('cxReplyModal');
  if (host) host.innerHTML = '';
}

export async function sendReply(id) {
  const subject = (document.getElementById('cxReplySubject') || {}).value || '';
  const bodyText = (document.getElementById('cxReplyBody') || {}).value || '';
  if (!bodyText.trim()) { if (window.showToast) window.showToast('Write the reply first'); return; }
  try {
    const res = await api('/api/admin/customer-feedback/reply', { method: 'POST', body: JSON.stringify({ id: id, subject: subject, body: bodyText }) });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      if (window.showToast) window.showToast((data && data.error) || 'The reply was NOT sent');
      return;
    }
    closeReply();
    if (window.showToast) window.showToast('Reply sent from ' + (data.sent_as || 'the store') + ' ✓', 'success');
    render();
  } catch (e) {
    if (window.showToast) window.showToast('Network error — the reply was NOT sent');
  }
}

// v130.8 — mark a review as actioned (leaves the Needs-action queue; nothing
// is deleted). The Worker scope-checks; an AM can action their own area.
export async function markReviewed(id) {
  try {
    const res = await api('/api/admin/customer-feedback/mark-reviewed', { method: 'POST', body: JSON.stringify({ id: id }) });
    if (!res.ok) throw new Error('status ' + res.status);
    if (typeof window !== 'undefined' && window.showToast) window.showToast('Marked as actioned', 'success');
    render();
  } catch (e) {
    if (typeof window !== 'undefined' && window.showToast) window.showToast('Could not update (' + ((e && e.message) || e) + ')');
  }
}

// ── B2: period view (toggle + summary strip + stores × period grid) ──────────
// Same filters as the scorecard (country/area/month/rating), plus &period=…, on
// the B1 timeline endpoint. Absolute Worker URL via api() → DB.workerUrl().
function timelineQS() {
  const base = filterQS();               // '' | '?a=b&c=d'
  return (base ? base + '&' : '?') + 'period=' + encodeURIComponent(_period);
}

// Segmented Week / Month / Quarter toggle.
function periodToggle() {
  const btn = (p, label) => {
    const on = p === _period;
    return '<button onclick="CxFeedback.setPeriod(\'' + p + '\')" ' +
      'style="border:1px solid var(--border-strong);background:' + (on ? 'var(--text)' : 'var(--card)') + ';color:' + (on ? 'var(--card)' : 'var(--text)') +
      ';padding:6px 14px;font-size:13px;font-weight:700;cursor:pointer' +
      (p === 'week' ? ';border-radius:8px 0 0 8px' : p === 'quarter' ? ';border-radius:0 8px 8px 0;border-left:none' : ';border-left:none') +
      '">' + esc(label) + '</button>';
  };
  return '<div style="display:inline-flex;margin-bottom:14px">' +
    btn('week', 'Week') + btn('month', 'Month') + btn('quarter', 'Quarter') + '</div>';
}

// Headline summary strip for the most recent period, with a ▲/▼ delta vs the
// previous period. Green ▲ up / red ▼ down; no arrow when only one period.
function summaryStrip(buckets) {
  const cur = buckets[0] || {};
  const delta = periodDelta(buckets);
  const arrow = (n, invert) => {
    if (n == null || n === 0) return '';
    const up = n > 0;
    const good = invert ? !up : up;           // for avg/nps, up is good
    const color = good ? '#059669' : '#DC2626';
    const glyph = up ? '▲' : '▼';    // ▲ / ▼
    const mag = Math.abs(n);
    return ' <span style="font-size:12px;font-weight:800;color:' + color + '">' + glyph + ' ' + mag + '</span>';
  };
  const tile = (label, value, deltaHtml) =>
    '<div style="flex:1;min-width:110px;background:var(--card);border:1px solid var(--border);border-radius:12px;padding:12px 14px;text-align:center">' +
      '<div style="font-size:26px;font-weight:900;line-height:1">' + value + (deltaHtml || '') + '</div>' +
      '<div style="font-size:11px;font-weight:700;color:var(--sub);margin-top:6px;text-transform:uppercase;letter-spacing:.4px">' + esc(label) + '</div>' +
    '</div>';
  const avg = (cur.avg == null) ? '—' : Number(cur.avg).toFixed(1);
  const nps = (cur.nps == null) ? '—' : (cur.nps > 0 ? '+' : '') + cur.nps;
  const periodWord = _period === 'week' ? 'week' : _period === 'quarter' ? 'quarter' : 'month';
  return '<div style="font-size:12px;color:var(--tiny);margin-bottom:8px">Latest ' + esc(periodWord) +
      ' — <b>' + esc(colLabel(cur.label)) + '</b>' + (delta ? ' vs previous ' + esc(periodWord) : '') + '</div>' +
    '<div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:18px">' +
      tile('Reviews', cur.count || 0, delta ? arrow(delta.count, false) : '') +
      tile('Avg score', avg, delta ? arrow(delta.avg, false) : '') +
      tile('NPS', nps, delta ? arrow(delta.nps, false) : '') +
    '</div>';
}

// The stores × period grid. Rows worst-recent first, All-stores pinned on top;
// columns oldest→newest; cells shaded by score band; count as muted subtext.
function periodGrid(buckets) {
  const { cols, allRow, rows } = pivotTimeline(buckets, colCap(_period));
  const cell = (c) => {
    if (!c || c.avg == null) {
      return '<td style="text-align:center;padding:8px 10px;color:var(--border-strong);border-bottom:1px solid var(--border)">–</td>';
    }
    const st = bandStyle(scoreBand(c.avg));
    return '<td style="text-align:center;padding:6px 10px;border-bottom:1px solid var(--border);background:' + st.bg + '">' +
      '<div style="font-size:15px;font-weight:800;color:' + st.fg + '">' + Number(c.avg).toFixed(1) + '</div>' +
      '<div style="font-size:10px;color:var(--sub)">' + (c.count || 0) + '</div></td>';
  };
  const nameCell = (label, bold) =>
    '<td' + (bold ? '' : ' translate="no"') + ' style="text-align:start;padding:8px 12px;border-bottom:1px solid var(--border);position:sticky;inset-inline-start:0;background:var(--card);' +
      (bold ? 'font-weight:800' : 'font-weight:600') + ';white-space:nowrap;min-width:130px">' + esc(label) + '</td>';
  const header = '<tr><th style="text-align:start;padding:8px 12px;position:sticky;inset-inline-start:0;background:var(--surface-2);font-size:11px;' +
      'text-transform:uppercase;letter-spacing:.4px;color:var(--sub);border-bottom:1px solid var(--border);min-width:130px">Store</th>' +
    cols.map((c) => '<th style="text-align:center;padding:8px 10px;font-size:11px;font-weight:800;color:var(--text);' +
      'border-bottom:1px solid var(--border);background:var(--surface-2);min-width:56px">' + esc(c.colLabel) + '</th>').join('') + '</tr>';
  const allTr = '<tr style="background:var(--surface-2)">' + nameCell(allRow.store, true) + allRow.cells.map(cell).join('') + '</tr>';
  const storeTrs = rows.map((r) => '<tr>' + nameCell(r.store, false) + r.cells.map(cell).join('') + '</tr>').join('');
  return '<div style="overflow-x:auto;-webkit-overflow-scrolling:touch;border:1px solid var(--border);border-radius:12px">' +
    '<table style="border-collapse:collapse;width:100%;background:var(--card)">' +
    '<thead>' + header + '</thead><tbody>' + allTr + storeTrs + '</tbody></table></div>' +
    '<div style="font-size:11px;color:var(--sub);margin-top:6px">Cell = average score (small number = reviews that ' +
      esc(_period === 'week' ? 'week' : _period === 'quarter' ? 'quarter' : 'month') +
      '). Green ≥ 8.5 · amber 7.0–8.4 · red < 7.0. Worst-scoring store on top.</div>';
}

export async function renderTimeline() {
  const pane = timelineRoot();
  if (!pane) return;
  pane.innerHTML = periodToggle() +
    '<div style="padding:16px;color:var(--tiny)">Loading…</div>';
  let data;
  try {
    const res = await api('/api/admin/customer-feedback/timeline' + timelineQS());
    if (res.status === 401 || res.status === 403) {
      pane.innerHTML = periodToggle() + '<div style="padding:16px;color:var(--adm-red-text)">Your session looks expired — sign out and back in.</div>';
      return;
    }
    if (!res.ok) throw new Error('status ' + res.status);
    data = await res.json();
  } catch (e) {
    // Keep the tab usable — surface a toast, leave the toggle in place.
    if (typeof window !== 'undefined' && window.showToast) window.showToast('Could not load the period view (' + ((e && e.message) || e) + ').');
    pane.innerHTML = periodToggle() + '<div style="padding:16px;color:var(--adm-red-text)">Could not load the period view.</div>';
    return;
  }
  const buckets = (data && data.buckets) || [];
  if (!buckets.length) {
    pane.innerHTML = periodToggle() +
      '<div style="padding:20px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px;margin-bottom:18px">No reviews in this period yet.</div>';
    return;
  }
  pane.innerHTML = periodToggle() + summaryStrip(buckets) + periodGrid(buckets) +
    '<div style="height:20px"></div>';
}

function scoreTile(label, value, sub, color) {
  return '<div style="flex:1;min-width:120px;background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px;text-align:center">' +
    '<div style="font-size:32px;font-weight:900;line-height:1;color:' + color + '">' + value + '</div>' +
    '<div style="font-size:12px;font-weight:700;margin-top:6px">' + esc(label) + '</div>' +
    (sub ? '<div style="font-size:11px;color:var(--sub)">' + esc(sub) + '</div>' : '') + '</div>';
}

function draw(el, data) {
  const sc = data.scorecard || { overall: { count: 0, avg: null, nps: null }, byStore: [] };
  const o = sc.overall || {};
  const head = '<div style="margin-bottom:14px"><div style="font-size:18px;font-weight:800">Customer Feedback' +
    (data.scoped ? ' <span style="font-size:12px;color:var(--sub);font-weight:600">(your area)</span>' : '') + '</div>' +
    '<div style="font-size:12px;color:var(--tiny)">From the in-store / receipt QR survey. Worst-scoring stores first.</div>' +
    (data.scoped ? '<div style="font-size:12px;color:var(--sub);margin-top:4px">⏳ Store view shows feedback on a 1-hour delay — Head Office sees it live.</div>' : '') + '</div>';

  const isHO = data.scoped === false;
  // Download comments (CSV) — available to HO and AMs; the Worker decides the columns
  // (HO gets contact details, an AM doesn't). Sits alongside the HO survey editor.
  const downloadBtn = '<button onclick="CxFeedback.downloadComments()" ' +
    'style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:7px 14px;font-size:13px;font-weight:600;cursor:pointer">⬇ Download comments</button>';
  // B4: "Analyse this period" — on-demand AI themes + call-outs for the current
  // period + filters. Available to HO and AMs (the Worker scopes an AM to their area).
  const analyseBtn = '<button onclick="CxFeedback.analyse()" ' +
    'style="background:var(--text);color:var(--card);border:1px solid var(--text);border-radius:8px;padding:7px 14px;font-size:13px;font-weight:700;cursor:pointer">✨ Analyse this period</button>';
  const surveyTools =
    '<div style="margin-bottom:14px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">' +
      analyseBtn +
      downloadBtn +
    '</div>' +
    '<div id="cxAiRoot" style="margin-bottom:14px"></div>' +
    '';

  if (!o.count) {
    // Keep the filter bar visible when a filter has emptied the results, so the
    // user can adjust or Clear — and say WHY it's empty.
    const msg = anyFilterActive()
      ? 'No customer feedback matches these filters.'
      : 'No customer feedback yet. Once the survey QR is live, scores appear here.';
    el.innerHTML = head + surveyTools + filterBar(data) + '<div style="padding:20px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">' + esc(msg) + '</div>';
    return;
  }

  const tiles = '<div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:18px">' +
    scoreTile('Avg rating', (o.avg == null ? '—' : o.avg) + '<span style="font-size:16px;color:var(--tiny)">/10</span>', o.count + ' response' + (o.count === 1 ? '' : 's'), ratingColor(o.avg)) +
    scoreTile('NPS', (o.nps == null ? '—' : (o.nps > 0 ? '+' : '') + o.nps), o.promoters + ' promoters · ' + o.detractors + ' detractors', npsColor(o.nps)) +
    '</div>';

  const stores = (sc.byStore || []).map((s) => {
    const w = Math.max(0, Math.min(100, ((Number(s.avg) || 0) / 10) * 100));
    return '<div style="border:1px solid var(--border);border-radius:10px;padding:12px;margin-bottom:8px;background:var(--card)">' +
      '<div style="display:flex;justify-content:space-between;gap:8px;align-items:center">' +
        '<div translate="no" style="font-weight:700">' + esc(s.store) + '</div>' +
        '<div style="font-size:13px"><b style="color:' + ratingColor(s.avg) + '">' + (s.avg == null ? '—' : s.avg) + '</b>/10 · NPS <b style="color:' + npsColor(s.nps) + '">' + (s.nps == null ? '—' : s.nps) + '</b> · ' + s.count + '</div>' +
      '</div>' +
      '<div style="height:6px;background:var(--border);border-radius:3px;overflow:hidden;margin-top:8px"><div style="height:100%;width:' + w + '%;background:' + ratingColor(s.avg) + '"></div></div>' +
    '</div>';
  }).join('');

  const canDelete = data.scoped === false;   // Head Office only (area managers can view, not delete)

  // One review card — shared by the Reviews list and the Needs-action queue
  // (the queue adds a "✓ Done" action). v123.27 fraud transparency unchanged;
  // v130.8 adds the mall-wifi caveat to the duplicate flag (shared IPs are
  // normal: mall guest wifi + carrier NAT — the flag now means "within 24h").
  const reviewCard = (r, withDone) => {
    // v136.8 (accessibility audit): "float:right;float:inline-end" puts the buttons at the end of the line in Arabic
    // too; a browser that does not know inline-end keeps right, so English is unchanged either way.
    const del = (canDelete && r.id)
      ? '<button onclick="CxFeedback.del(' + r.id + ')" title="Delete this review" aria-label="Delete this review" ' +
        'style="float:right;float:inline-end;border:none;background:none;color:var(--adm-red-text);cursor:pointer;font-size:15px;line-height:1;padding:0 2px">✕</button>'
      : '';
    const done = (withDone && r.id)
      ? '<button onclick="CxFeedback.markReviewed(' + r.id + ')" title="Mark as actioned — leaves this queue, nothing is deleted" ' +
        'style="float:right;float:inline-end;border:1px solid #059669;background:var(--card);color:var(--adm-ok-text);border-radius:8px;padding:3px 10px;font-size:12px;font-weight:700;cursor:pointer;margin-inline-start:6px">✓ Done</button>'
      : '';
    // v130.9 — the reply affordance: HO only, contact on file, not yet replied.
    const reply = (canDelete && r.id && r.has_contact && !r.replied_at)
      ? '<button onclick="CxFeedback.openReply(' + r.id + ')" title="Reply to the customer by email — they left their details" ' +
        'style="float:right;float:inline-end;border:1px solid #1d4ed8;background:var(--card);color:var(--info-deep);border-radius:8px;padding:3px 10px;font-size:12px;font-weight:700;cursor:pointer;margin-inline-start:6px">↩ Reply</button>'
      : '';
    const repliedStamp = r.replied_at
      ? '<div style="font-size:11px;color:var(--adm-ok-text);margin-top:3px">↩ Replied by ' + esc(r.replied_by || '') + ' · ' + esc(fmtWhen(r.replied_at)) + '</div>'
      : '';
    const ooc = r.flags && r.flags.indexOf('out_of_country') !== -1;
    const heavy = r.flags && r.flags.indexOf('ip_heavy') !== -1;
    const dup = r.flags && r.flags.indexOf('store_dup') !== -1;
    const riskBadge = r.fraud
      ? ' <span style="font-size:10px;font-weight:800;text-transform:uppercase;padding:1px 6px;border-radius:5px;background:' +
        (r.fraud === 'high' ? 'var(--danger-bg)' : 'var(--warn-bg)') + ';color:' + (r.fraud === 'high' ? 'var(--danger-deep)' : 'var(--warn-deep)') + '">⚠ ' + esc(r.fraud) + ' risk</span>'
      : '';
    let flagLine = '';
    if (r.flags && r.flags.length) {
      const bits = [];
      if (ooc) bits.push('outside UAE/Qatar' + (r.country ? ' (' + esc(r.country) + ')' : ''));
      if (dup) bits.push('same IP reviewed this store ' + (r.store_dup_count || 2) + '× within 24h');
      if (heavy) bits.push('this IP left ' + (r.ip_count || 6) + ' reviews');
      const ipTag = r.ip ? ' · IP <span style="font-family:monospace">' + esc(r.ip) + '</span>' : '';
      flagLine = '<div style="font-size:11px;color:var(--adm-amber-text);margin-top:3px">' + bits.join(' · ') + ipTag + '</div>';
    }
    return '<div style="border-inline-start:3px solid ' + ratingColor(r.rating) + ';padding:6px 0 6px 10px;margin-bottom:8px">' +
      del + done + reply +
      '<div style="font-size:12px;color:var(--sub)">' + esc(fmtWhen(r.submitted_at)) + ' · ' + esc(r.store || 'No store') + ' · ' + r.rating + '/10' + riskBadge + '</div>' +
      flagLine + repliedStamp +
      (r.comment ? '<div translate="no" style="font-size:14px;white-space:pre-wrap">' + esc(r.comment) + '</div>'
                 : '<div style="font-size:13px;color:var(--tiny);font-style:italic">No comment left — score only.</div>') +
    '</div>';
  };

  const comments = (data.recent || []).slice(0, 60).map((r) => reviewCard(r, false)).join('');
  const flaggedNote = data.flaggedCount
    ? ' <span style="color:var(--adm-amber-text);text-transform:none;letter-spacing:0">· ' + data.flaggedCount + ' flagged ⚠</span>' : '';
  // v132.37: a low score with nothing written now appears in the list. Say so
  // in the heading, so somebody who meets their first one knows it is real
  // and not a card that failed to load.
  const silentNote = data.silentLowCount
    ? ' <span style="color:var(--adm-amber-text);text-transform:none;letter-spacing:0">· ' + data.silentLowCount +
      ' low score' + (data.silentLowCount === 1 ? '' : 's') + ' with no comment</span>' : '';
  const sectionTitle = (t) => '<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--tiny);margin:6px 0 8px">' + t + '</div>';

  // ── v130.8: compose per sub-tab (spec #1214) ───────────────────────────────
  if (_tab === 'reviews') {
    el.innerHTML = head + filterBar(data) +
      sectionTitle('All feedback' + silentNote + flaggedNote) +
      (comments || '<div style="padding:16px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">Nothing matches these filters.</div>');
    return;
  }
  if (_tab === 'action') {
    const queue = (data.action || []).map((r) => reviewCard(r, true)).join('');
    el.innerHTML = head + filterBar(data) +
      sectionTitle('⚠️ Needs action — new reviews rated 5 or under, or risk-flagged · oldest first') +
      '<div style="font-size:12px;color:var(--sub);margin-bottom:10px">✓ Done marks a review as handled and takes it off this list (nothing is deleted). ' +
        'Duplicate-IP flags mean same store within 24h — shared mall wifi and mobile networks make repeat IPs across days normal.</div>' +
      (queue || '<div style="padding:16px;color:var(--adm-ok-text);border:1px dashed var(--adm-ok-line);border-radius:10px;background:var(--adm-ok-pale)">✅ Nothing needs action — every low or flagged review has been handled.</div>');
    return;
  }
  if (_tab === 'insights') {
    el.innerHTML = head +
      '<div style="margin-bottom:14px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">' + analyseBtn + downloadBtn + '</div>' +
      '<div id="cxAiRoot" style="margin-bottom:14px"></div>' +
      filterBar(data) +
      '<div style="font-size:12px;color:var(--sub)">✨ Analyse looks at the comments matching the filters above for the current period.</div>';
    return;
  }
  if (_tab === 'links') {
    // The store list comes from the scorecard the tab already loaded, so this
    // adds no fetch and shows exactly the stores this viewer is scoped to.
    const names = ((data.scorecard && data.scorecard.byStore) || []).map((r) => r.store).filter(Boolean).sort();
    el.innerHTML = head + sectionTitle('Store QR links') + linksHtml(names);
    return;
  }
  // scorecard (default)
  el.innerHTML = head + filterBar(data) + tiles +
    sectionTitle('By store') + stores;
}
